import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { IonContent } from '@ionic/angular/standalone';
import { HeaderComponent } from 'src/components/header/header-default.component';
import { Const } from 'src/definitions/global.definition';
import { _Step1Component, ProfileForm } from './components/step-1.component';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Form, FormError } from 'src/definitions/form.definition';
import { _Step2Component, TanggunganForm } from './components/step-2.component';
import { _Step3Component, ContactForm } from './components/step-3.component';
import { _Step4Component, PositionForm } from './components/step-4.component';
import { _Step5Component, FamilyForm } from './components/step-5.component';
import { FormService, MaxDigits, UrlValidator } from 'src/services/form.service';
import { ToastService } from 'src/services/toast.service';
import { GlobalService } from 'src/services/global.service';
import { RestService } from 'src/services/rest.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute, CanDeactivateFn, Router, RouterStateSnapshot } from '@angular/router';
import {
  CreateKaryawanForm,
  GetKaryawanDetailResponse,
  LivingStatus,
} from 'src/definitions/karyawan.definition';
import { DialogConfirmComponent } from 'src/components/dialog/dialog-confirm.component';

type KaryawanForm = {
  profile: ProfileForm;
  tanggungan: TanggunganForm;
  contact: ContactForm;
  position: PositionForm;
  family: FamilyForm;
};

type BankForm = {
  bank_name: string;
  account_number: string;
  account_name: string;
  is_primary: string;
};

type KontakDaruratForm = {
  name: string;
  phone_ext: string;
  phone: string | null;
  relation: string;
  address: string;
};

@Component({
  selector: 'app-karyawan-add',
  templateUrl: './karyawan-add.page.html',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    IonContent,
    HeaderComponent,
    DialogConfirmComponent,
    _Step1Component,
    _Step2Component,
    _Step3Component,
    _Step4Component,
    _Step5Component,
  ],
})
export class KaryawanAddPage implements OnInit, OnDestroy {
  karyawanUuid: string = '';
  karyawanUuid$?: Subscription;

  karyawan?: any;

  currentStep: Const<typeof Step> = Step.DATA_DIRI;
  steps = Object.values(Step).map((step) => ({
    value: step,
    label: StepLabel[step],
  }));

  form: Form<KaryawanForm> = new FormBuilder().nonNullable.group({
    profile: new FormBuilder().nonNullable.group({
      image: [''],
      name: ['', [Validators.required]],
      nickname: [''],
      nationality: ['Indonesia', [Validators.required]],
      birthday: ['', [Validators.required]],
      place_of_birth: ['', [Validators.required]],
      identity_number: [null as string | null, [Validators.required]],
      religion: ['', [Validators.required]],
      gender: ['', [Validators.required]],
      address_original: ['', [Validators.required]],
      blood_type: [''],
      npwp_number: [null as string | null],
      npwp_image: [''],
      ktp_image: [''],
      banks: new FormBuilder().nonNullable.array([
        new FormBuilder().nonNullable.group({
          bank_name: ['', [Validators.required]],
          account_number: [null as string | null, [Validators.required]],
          account_name: ['', [Validators.required]],
          is_primary: ['true'],
        }),
      ]),
      kontak_darurat: new FormBuilder().nonNullable.array([] as Form<KontakDaruratForm>[]),
      nama_faskes: [''],
      kode_faskes: [null as string | null],
      weight: [null as number | null],
      height: [null as number | null],
      shirt_size: [''],
      pants_size: [null as number | null],
      shoe_size: [null as number | null],
      has_medical_history: [''],
      medical_history: [''],
    }),
    tanggungan: new FormBuilder().nonNullable.group({
      martial_status: ['', [Validators.required]],
      has_children: ['', [Validators.required]],
      children_count: [null as number | null],
      living_status: ['', [Validators.required]],
      address_current: ['', [Validators.required]],
      ownership_status: [''],
      rent_amount: [null as number | null],
    }),
    contact: new FormBuilder().nonNullable.group({
      phone_ext: ['62', [Validators.required]],
      phone: [null as string | null, [Validators.required, MaxDigits(13)]],
      email: ['', [Validators.required, Validators.email]],
      instagram_url: ['', [UrlValidator]],
      facebook_url: ['', [UrlValidator]],
      tiktok_url: ['', [UrlValidator]],
    }),
    position: new FormBuilder().nonNullable.group({
      divisi_id: ['', [Validators.required]],
      sub_divisi_id: [''],
      level_id: ['', [Validators.required]],
      lokasi_id: ['', [Validators.required]],
      kontrak_id: ['', [Validators.required]],
      join_date: ['', [Validators.required]],
      saldo_cuti_awal: [null as number | null, [Validators.required]],
    }),
    family: new FormBuilder().nonNullable.group({
      kk_number: [null as string | null, [Validators.required]],
      kk_image: ['', [Validators.required]],
      keluarga: new FormBuilder().nonNullable.array([
        new FormBuilder().nonNullable.group({
          name: ['', [Validators.required]],
          phone_ext: ['62', [Validators.required]],
          phone: [null as string | null, [Validators.required, MaxDigits(13)]],
          relation: ['', [Validators.required]],
          gender: ['', [Validators.required]],
        }),
      ]),
    }),
  });
  formError: FormError<KaryawanForm> = { ...DefaultFormError };
  formSubmitting: boolean = false;

  hasUnsavedChanges: boolean = true;

  leaveTarget?: RouterStateSnapshot;
  confirmLeaveVisible: boolean = false;

  constructor(
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
    private formService: FormService,
    private restService: RestService,
    private toastService: ToastService,
    private globalService: GlobalService,
  ) {}

  ngOnInit(): void {
    this.karyawanUuid$ = this.route.params.subscribe((params) => {
      this.karyawanUuid = params['uuid'] ?? '';
    });
  }

  ngOnDestroy(): void {
    this.karyawanUuid$?.unsubscribe();
  }

  ionViewDidEnter() {
    if (this.karyawanUuid) {
      this.__getKaryawan();

      const saldoCutiAwalField = this.form.controls.position.controls.saldo_cuti_awal;
      saldoCutiAwalField.setValidators([]);
      saldoCutiAwalField.updateValueAndValidity();
    }
  }

  addBankField(bank?: GetKaryawanDetailResponse['data']['bank'][number]) {
    this.form.controls.profile.controls.banks.push(
      new FormBuilder().nonNullable.group({
        bank_name: [bank?.bank_name ?? '', [Validators.required]],
        account_number: [bank?.account_number ?? null, [Validators.required]],
        account_name: [bank?.account_name ?? '', [Validators.required]],
        is_primary: [bank?.is_primary ? 'true' : 'false'],
      }),
    );

    this.formError.profile.banks[this.form.controls.profile.controls.banks.length - 1] = {
      ...DefaultBankFormError,
    };
  }

  removeBankField(index: number) {
    this.form.controls.profile.controls.banks.removeAt(index);

    const length = this.form.controls.profile.controls.banks.length;
    for (let i = index; i < length; i++)
      this.formError.profile.banks[i] = this.formError.profile.banks[i + 1];
    delete this.formError.profile.banks[length];
  }

  addFamilyField(family?: GetKaryawanDetailResponse['data']['keluarga'][number]) {
    this.form.controls.family.controls.keluarga.push(
      new FormBuilder().nonNullable.group({
        name: [family?.name ?? '', [Validators.required]],
        phone_ext: [family?.phone_ext ?? '62', [Validators.required]],
        phone: [family?.phone ?? null, [Validators.required, MaxDigits(13)]],
        relation: [family?.relation ?? '', [Validators.required]],
        gender: [family?.gender ?? '', [Validators.required]],
      }),
    );

    this.formError.family.keluarga[
      this.form.controls.family.controls.keluarga.controls.length - 1
    ] = {
      ...DefaultKeluargaFormError,
    };
  }

  removeFamilyField(index: number) {
    this.form.controls.family.controls.keluarga.removeAt(index);

    const length = this.form.controls.family.controls.keluarga.controls.length;
    for (let i = index; i < length; i++)
      this.formError.family.keluarga[i] = this.formError.family.keluarga[i + 1];
    delete this.formError.family.keluarga[length];
  }

  addKontakDaruratField(
    kontak_darurat?: GetKaryawanDetailResponse['data']['kontak_darurat'][number],
  ) {
    this.form.controls.profile.controls.kontak_darurat.push(
      new FormBuilder().nonNullable.group({
        name: [kontak_darurat?.name ?? '', [Validators.required]],
        phone_ext: [kontak_darurat?.phone_ext ?? '62', [Validators.required]],
        phone: [kontak_darurat?.phone ?? null, [Validators.required, MaxDigits(13)]],
        relation: [kontak_darurat?.relation ?? '', [Validators.required]],
        address: [kontak_darurat?.address ?? '', [Validators.required]],
      }),
    );

    this.formError.profile.kontak_darurat[
      this.form.controls.profile.controls.kontak_darurat.length - 1
    ] = {
      ...DefaultKontakDaruratFormError,
    };
  }

  removeKontakDaruratField(index: number) {
    this.form.controls.profile.controls.kontak_darurat.removeAt(index);

    const length = this.form.controls.profile.controls.kontak_darurat.length;
    for (let i = index; i < length; i++)
      this.formError.profile.kontak_darurat[i] = this.formError.profile.kontak_darurat[i + 1];
    delete this.formError.profile.kontak_darurat[length];
  }

  showConfirmLeave() {
    this.confirmLeaveVisible = true;
  }

  hideConfirmLeave() {
    this.confirmLeaveVisible = false;
  }

  confirmLeave() {
    this.hasUnsavedChanges = false;
    this.hideConfirmLeave();
    this.router.navigate([this.leaveTarget?.url ?? ''], { replaceUrl: true });
  }

  async submit() {
    const raw = { ...this.form.getRawValue() };
    const payload: CreateKaryawanForm = {
      ...raw.profile,
      ...raw.tanggungan,
      ...raw.contact,
      ...raw.position,
      has_medical_history: raw.profile.has_medical_history === 'true',
      banks: raw.profile.banks.map((bank) => ({
        ...bank,
        account_number: bank.account_number?.toString() ?? '',
        is_primary: bank.is_primary === 'true',
      })),
      kontak_darurat: raw.profile.kontak_darurat.map((kontak_darurat) => ({
        ...kontak_darurat,
        phone: kontak_darurat.phone?.toString() ?? '',
      })),
      kk_number: raw.family.kk_number ? raw.family.kk_number.toString() : '',
      kk_image: raw.family.kk_image.includes('https') ? '' : raw.family.kk_image,
      keluarga: this.form.getRawValue().family.keluarga.map((family) => ({
        ...family,
        phone: family.phone?.toString() ?? '',
      })),
      has_children: this.form.getRawValue().tanggungan.has_children === 'true',
    };

    if (this.karyawanUuid) {
      this.__update(payload);
    } else {
      this.__create(payload);
    }
  }

  private async __create(payload: CreateKaryawanForm) {
    try {
      this.globalService.showLoading();

      const response = await lastValueFrom(this.restService.createKaryawan(payload));

      this.toastService.showToast({
        summary: 'Berhasil ditambahkan',
        detail: 'Data karyawan berhasil disimpan',
        severity: 'success',
      });

      this.hasUnsavedChanges = false;

      this.router.navigate(['/karyawan/' + response.data.uuid], {
        replaceUrl: true,
      });
    } catch (error) {
      error = this.formService.serverErrorHandler(error, this.formError);
      this.globalService.errorHandler(error, 'Gagal membuat karyawan');
    } finally {
      this.globalService.hideLoading();
    }
  }

  private async __update(payload: CreateKaryawanForm) {
    try {
      this.globalService.showLoading();

      const editPayload = this.__generateEditPayload(payload);
      const response = await lastValueFrom(
        this.restService.updateKaryawan(this.karyawanUuid, editPayload),
      );

      this.toastService.showToast({
        summary: 'Berhasil diperbarui',
        detail: 'Data karyawan berhasil diperbarui',
        severity: 'success',
      });

      this.hasUnsavedChanges = false;

      this.router.navigate(['/karyawan/' + response.data.uuid], {
        replaceUrl: true,
      });
    } catch (error) {
      error = this.formService.serverErrorHandler(error, this.formError);
      this.globalService.errorHandler(error, 'Gagal mengupdate karyawan');
    } finally {
      this.globalService.hideLoading();
    }
  }

  private async __getKaryawan() {
    try {
      this.globalService.showLoading();
      const response = await lastValueFrom(this.restService.getKaryawanDetail(this.karyawanUuid));
      this.karyawan = response.data;

      this.__setupEdit(response.data);
    } catch (error) {
      this.globalService.errorHandler(error, 'Gagal mendapatkan data karyawan');
    } finally {
      this.globalService.hideLoading();
    }
  }

  private __setupEdit(karyawan: GetKaryawanDetailResponse['data']) {
    // Step 1
    this.form.controls.profile.patchValue({
      image: karyawan.image ?? '',
      name: karyawan.name,
      nickname: karyawan.nickname ?? '',
      nationality: karyawan.nationality,
      birthday: karyawan.birthday,
      place_of_birth: karyawan.place_of_birth,
      identity_number: karyawan.identity_number,
      religion: karyawan.religion,
      gender: karyawan.gender,
      address_original: karyawan.address_original,
      blood_type: karyawan.blood_type,
      npwp_number: karyawan.npwp_number,
      npwp_image: karyawan.npwp_image ?? '',
      ktp_image: karyawan.ktp_image ?? '',
      nama_faskes: karyawan.nama_faskes,
      kode_faskes: karyawan.kode_faskes,
      weight: karyawan.weight ? parseFloat(karyawan.weight) : null,
      height: karyawan.height ? parseFloat(karyawan.height) : null,
      shirt_size: karyawan.shirt_size ?? '',
      pants_size: karyawan.pants_size ? parseFloat(karyawan.pants_size) : null,
      shoe_size: karyawan.shoe_size ? parseFloat(karyawan.shoe_size) : null,
      has_medical_history: karyawan.medical_history ? 'true' : 'false',
      medical_history: karyawan.medical_history ?? '',
    });

    if (karyawan.bank.length) {
      karyawan.bank.forEach((bank) => {
        this.addBankField(bank);
      });
      this.removeBankField(0);
    }

    if (karyawan.kontak_darurat.length) {
      karyawan.kontak_darurat.forEach((kontak_darurat) => {
        this.addKontakDaruratField(kontak_darurat);
      });
    }

    // Step 2
    this.form.controls.tanggungan.patchValue({
      martial_status: karyawan.martial_status,
      has_children: karyawan.has_children ? 'true' : 'false',
      children_count: karyawan.children_count,
      living_status: karyawan.living_status,
      address_current: karyawan.address_current,
      ownership_status: karyawan.ownership_status ?? '',
      rent_amount: karyawan.rent_amount,
    });

    if (karyawan.has_children) {
      this.form.controls.tanggungan.controls.children_count.setValidators([Validators.required]);
      this.form.controls.tanggungan.controls.children_count.updateValueAndValidity();
    } else {
      this.form.controls.tanggungan.controls.children_count.clearValidators();
      this.form.controls.tanggungan.controls.children_count.updateValueAndValidity();
    }

    if (karyawan.living_status === LivingStatus.PRIBADI) {
      this.form.controls.tanggungan.controls.ownership_status.setValidators([Validators.required]);
      this.form.controls.tanggungan.controls.ownership_status.updateValueAndValidity();
    } else {
      this.form.controls.tanggungan.controls.ownership_status.clearValidators();
      this.form.controls.tanggungan.controls.ownership_status.updateValueAndValidity();
    }

    if (karyawan.living_status === LivingStatus.KONTRAK) {
      this.form.controls.tanggungan.controls.rent_amount.setValidators([Validators.required]);
      this.form.controls.tanggungan.controls.rent_amount.updateValueAndValidity();
    } else {
      this.form.controls.tanggungan.controls.rent_amount.clearValidators();
      this.form.controls.tanggungan.controls.rent_amount.updateValueAndValidity();
    }

    // Step 3
    this.form.controls.contact.patchValue({
      phone_ext: karyawan.phone_ext,
      phone: karyawan.phone,
      email: karyawan.email ?? '',
      instagram_url: karyawan.instagram_url ?? '',
      facebook_url: karyawan.facebook_url ?? '',
      tiktok_url: karyawan.tiktok_url ?? '',
    });

    // Step 4
    this.form.controls.position.patchValue({
      divisi_id: karyawan.divisi?.id?.toString(),
      sub_divisi_id: karyawan.sub_divisi?.id?.toString(),
      level_id: karyawan.jabatan?.id?.toString(),
      lokasi_id: karyawan.lokasi?.id?.toString(),
      kontrak_id: karyawan.kontrak?.id?.toString(),
      join_date: karyawan.join_date ?? '',
      saldo_cuti_awal: karyawan.saldo_cuti_awal ?? null,
    });

    // Step 5
    this.form.controls.family.patchValue({
      kk_number: karyawan.kk_number ?? null,
      kk_image: karyawan.kk_image ?? '',
    });

    if (karyawan.keluarga.length) {
      karyawan.keluarga.forEach((keluarga) => {
        this.addFamilyField(keluarga);
      });
      this.removeFamilyField(0);
    }
  }

  private __generateEditPayload(payload: CreateKaryawanForm): CreateKaryawanForm {
    return {
      ...payload,
      ...(!this.__isNewImage(payload.image) && { image: '' }),
      ...(!this.__isNewImage(payload.ktp_image) && { ktp_image: '' }),
      ...(!this.__isNewImage(payload.npwp_image) && { npwp_image: '' }),
    };
  }

  private __isNewImage(image: string) {
    if (image.startsWith('https')) return false;
    return true;
  }
}

const Step = {
  DATA_DIRI: 1,
  TANGGUNGAN: 2,
  KONTAK: 3,
  POSISI: 4,
  DATA_KELUARGA: 5,
} as const;

const StepLabel: Record<string, string> = {
  [Step.DATA_DIRI]: 'Data Diri Karyawan',
  [Step.TANGGUNGAN]: 'Tanggungan',
  [Step.KONTAK]: 'Kontak Pribadi',
  [Step.POSISI]: 'Informasi Karyawan',
  [Step.DATA_KELUARGA]: 'Data Keluarga',
};

const DefaultKeluargaFormError: FormError<FamilyForm['keluarga'][number]> = {
  label: '',
  msg: '',
  name: { label: 'Nama', msg: '' },
  phone_ext: { label: 'Kode Telepon', msg: '' },
  phone: { label: 'No. Telepon', msg: '' },
  relation: { label: 'Hubungan dengan Karyawan Sebagai', msg: '' },
  gender: { label: 'Jenis Kelamin', msg: '' },
};

const DefaultBankFormError: FormError<BankForm> = {
  label: '',
  msg: '',
  bank_name: { label: 'Bank', msg: '' },
  account_number: { label: 'No. Rekening', msg: '' },
  account_name: { label: 'Nama Pemilik Rekening', msg: '' },
  is_primary: { label: 'Utama', msg: '' },
};

const DefaultKontakDaruratFormError: FormError<KontakDaruratForm> = {
  label: '',
  msg: '',
  name: { label: 'Nama', msg: '' },
  phone_ext: { label: 'Kode Telepon', msg: '' },
  phone: { label: 'No. Telepon', msg: '' },
  relation: { label: 'Hubungan dengan Karyawan Sebagai', msg: '' },
  address: { label: 'Jenis Kelamin', msg: '' },
};

const DefaultFormError: FormError<KaryawanForm> = {
  label: '',
  msg: '',
  profile: {
    label: '',
    msg: '',
    image: { label: 'Foto Profil', msg: '' },
    name: { label: 'Nama Lengkap', msg: '' },
    nickname: { label: 'Nama Panggilan', msg: '' },
    nationality: { label: 'Kewarganegaraan', msg: '' },
    birthday: { label: 'Tanggal Lahir', msg: '' },
    place_of_birth: { label: 'Tempat Lahir', msg: '' },
    identity_number: { label: 'No. ID/KTP', msg: '' },
    religion: { label: 'Agama', msg: '' },
    gender: { label: 'Jenis Kelamin', msg: '' },
    address_original: { label: 'Alamat KTP', msg: '' },
    blood_type: { label: 'Golongan Darah', msg: '' },
    npwp_number: { label: 'No. NPWP', msg: '' },
    npwp_image: { label: 'Foto NPWP', msg: '' },
    ktp_image: { label: 'Foto ID/KTP', msg: '' },
    banks: { label: 'Bank', msg: '', 0: { ...DefaultBankFormError } },
    kontak_darurat: { label: 'Kontak Darurat', msg: '', 0: { ...DefaultKontakDaruratFormError } },
    nama_faskes: { label: 'Alamat Faskes', msg: '' },
    kode_faskes: { label: 'Kode Faskes', msg: '' },
    weight: { label: 'Berat Badan', msg: '' },
    height: { label: 'Tinggi Badan', msg: '' },
    shirt_size: { label: 'Ukuran Baju', msg: '' },
    pants_size: { label: 'Ukuran Celana', msg: '' },
    shoe_size: { label: 'Ukuran Sepatu', msg: '' },
    has_medical_history: { label: 'Riwayat Penyakit', msg: '' },
    medical_history: { label: 'Nama Riwayat Penyakit', msg: '' },
  },
  tanggungan: {
    label: '',
    msg: '',
    martial_status: { label: 'Status Kawin', msg: '' },
    has_children: { label: 'Kepemilikan Anak', msg: '' },
    children_count: { label: 'Jumlah Anak', msg: '' },
    living_status: { label: 'Tempat Tinggal', msg: '' },
    address_current: { label: 'Alamat Saat Ini', msg: '' },
    ownership_status: { label: 'Kepemilikan', msg: '' },
    rent_amount: { label: 'Biaya Sewa', msg: '' },
  },
  contact: {
    label: '',
    msg: '',
    phone_ext: { label: 'Kode Telepon', msg: '' },
    phone: { label: 'No. Telepon', msg: '' },
    email: { label: 'Email', msg: '' },
    instagram_url: { label: 'Instagram', msg: '' },
    facebook_url: { label: 'Facebook', msg: '' },
    tiktok_url: { label: 'Tiktok', msg: '' },
  },
  position: {
    label: '',
    msg: '',
    divisi_id: { label: 'Divisi', msg: '' },
    sub_divisi_id: { label: 'Departemen', msg: '' },
    level_id: { label: 'Jabatan', msg: '' },
    lokasi_id: { label: 'Lokasi', msg: '' },
    kontrak_id: { label: 'Kontrak', msg: '' },
    join_date: { label: 'Tgl. Bergabung', msg: '' },
    saldo_cuti_awal: { label: 'Saldo Cuti Awal', msg: '' },
  },
  family: {
    label: '',
    msg: '',
    kk_number: { label: 'No. KK', msg: '' },
    kk_image: { label: 'Foto KK', msg: '' },
    keluarga: {
      label: '',
      msg: '',
      0: { ...DefaultKeluargaFormError },
    },
  },
};

export const KaryawanAddCanLeaveGuard: CanDeactivateFn<KaryawanAddPage> = (
  component,
  _,
  currentState,
  nextState,
) => {
  if (component.hasUnsavedChanges && currentState.url !== nextState.url) {
    component.leaveTarget = nextState;
    component.showConfirmLeave();
    return false;
  }
  return true;
};
